import { useState, type Dispatch, type SetStateAction } from "react";
import type { Activity, Level, Role, Tool } from "@app-types/activity-data.types";
import type { AppStep } from "@app-types/navigation.types";
import {
  getActivityOptionsForSelection,
  getLevelsForRoleTool,
  getRoles,
  getToolsForRole,
} from "@utils/helperFunctions";

export const useActivityFlow = (
  currentStep: AppStep,
  setCurrentStep: Dispatch<SetStateAction<AppStep>>,
) => {
  const [selectedRole, setSelectedRole] = useState<Role>();
  const [selectedTool, setSelectedTool] = useState<Tool>();
  const [selectedLevel, setSelectedLevel] = useState<Level>();
  const [selectedActivity, setSelectedActivity] = useState<
    Activity | undefined
  >();
  const [completedActivities, setCompletedActivities] = useState<
    { activityId: string; level: Level }[]
  >([]);
  const completedActivityIds = completedActivities.map(({ activityId }) => activityId);

  const roles = getRoles();
  const tools = selectedRole ? getToolsForRole(selectedRole) : [];
  const levels =
    selectedRole && selectedTool
      ? getLevelsForRoleTool(selectedRole, selectedTool)
      : [];

  const { availableActivities, recommendedActivities } =
    selectedRole && selectedTool && selectedLevel
      ? getActivityOptionsForSelection(
          selectedRole,
          selectedTool,
          selectedLevel,
          completedActivityIds,
        )
      : { availableActivities: [], recommendedActivities: [] };

  const goToDashboard = () => {
    setSelectedActivity(undefined);
    setCurrentStep(0);
  };

  const startPreferences = () => {
    setCurrentStep(1);
  };

  const findAnotherActivity = () => {
    setSelectedActivity(undefined);
    setCurrentStep(2);
  };

  const selectRole = (role: Role) => {
    setSelectedRole(selectedRole === role ? undefined : role);
  };

  const selectTool = (tool: Tool) => {
    setSelectedTool(selectedTool === tool ? undefined : tool);
  };

  const selectLevel = (level: Level) => {
    setSelectedLevel(selectedLevel === level ? undefined : level);
  };

  const selectActivity = (activity: Activity) => {
    setSelectedActivity(
      selectedActivity?.id === activity.id ? undefined : activity,
    );
  };

  const navigateToSelectedActivity = (activity: Activity, level: Level) => {
    setSelectedActivity(activity);
    setSelectedLevel(level);
    goBack();
  };

  const continuePreferences = () => {
    setCurrentStep((currentStep + 1) as AppStep);
  };

  const goBack = () => {
    setCurrentStep((step) => Math.max(1, step - 1) as AppStep);
  };

  const returnToTaskSelection = () => {
    setCurrentStep((currentStep - 1) as AppStep);
    setSelectedActivity(undefined);
  };

  const completeActivity = () => {
    if (!selectedActivity || !selectedLevel) {
      return;
    }

    setCompletedActivities((activities) =>
      activities.some(({ activityId }) => activityId === selectedActivity.id)
        ? activities
        : [...activities, { activityId: selectedActivity.id, level: selectedLevel }],
    );
    setCurrentStep(6);
  };

  return {
    currentStep,
    selectedRole,
    selectedTool,
    selectedLevel,
    selectedActivity,
    completedActivityIds,
    completedActivities,
    roles,
    tools,
    levels,
    availableActivities,
    recommendedActivities,
    goToDashboard,
    startPreferences,
    findAnotherActivity,
    selectRole,
    selectTool,
    selectLevel,
    selectActivity,
    continuePreferences,
    goBack,
    returnToTaskSelection,
    completeActivity,
    navigateToSelectedActivity,
  };
};
